import React from "react";
import { Input, Select, Checkbox, Space, Typography } from "antd";
import MediaPicker from "../../../components/mediaPicker";
import IconPicker from "../../../components/cms-common/IconPicker";
import {
  EMPTY_LINK,
  newId,
  listOps,
  FieldGrid,
  WrapRow,
  Field,
  PanelCard,
  ItemBox,
  ToggleRow,
  RowActions,
  AddItemButton,
  LinkField,
  LinkListEditor,
  PlaceholderList,
} from "./layoutFormKit";

const { Text } = Typography;

// What an action does on the site. Only "link" needs a destination; the others
// are wired to the site's cart drawer, wishlist and login/account features.
const ACTION_TYPES = [
  { value: "link", label: "Custom Link" },
  { value: "cart", label: "Cart (opens cart drawer)" },
  { value: "wishlist", label: "Wishlist" },
  { value: "login", label: "Sign In / Account" },
  { value: "signup", label: "Sign Up (hidden when signed in)" },
];

// Signed-in account dropdown. Mirrors the customer site's built-in list,
// which it uses while this list is empty. "Log Out" is always added last.
const DEFAULT_ACCOUNT_MENU = [
  ["Profile", "user-line", "/customer-dashboard/profile"],
  ["My Orders", "shopping-cart-line", "/customer-dashboard/my-order"],
  ["My Credit Points", "coupon-3-line", "/customer-dashboard/my-credit-points"],
  ["My Addresses", "contacts-book-line", "/customer-dashboard/my-address"],
  ["Reset Password", "lock-password-line", "/customer-dashboard/reset-password"],
];

const Header = ({ data = {}, onChange }) => {
  const set = (field, value) => onChange({ ...data, [field]: value });
  const setIn = (group, field, value) =>
    onChange({ ...data, [group]: { ...(data[group] || {}), [field]: value } });

  const search = data.search || {};
  const customerCare = data.customer_care || {};
  const navigation = data.navigation || [];
  const nav = listOps(navigation, (next) => set("navigation", next));
  const actions = data.actions || [];
  const acts = listOps(actions, (next) => set("actions", next));
  const accountMenu = data.account_menu || [];
  const account = listOps(accountMenu, (next) => set("account_menu", next));
  const loadDefaultAccountMenu = () =>
    set(
      "account_menu",
      DEFAULT_ACCOUNT_MENU.map(([label, icon, url]) => ({
        id: newId("acc"),
        label,
        icon,
        link: { type: "internal", url },
      })),
    );

  return (
    <div style={{ padding: "8px 0" }}>
      {/* BRANDING */}
      <PanelCard icon="image-line" color="#1890ff" title="Logo & Branding">
        <WrapRow align="flex-start" gap={16}>
          <Field label="Header Logo" flex="0 0 auto">
            <MediaPicker max={1} value={data.logo || []} onChange={(val) => set("logo", val)} />
          </Field>
          <Space direction="vertical" size={16} style={{ flex: "1 1 260px", minWidth: 0 }}>
            <Field label="Logo Alt Text">
              <Input
                value={data.logo_alt || ""}
                onChange={(e) => set("logo_alt", e.target.value)}
                placeholder="e.g. Infotainment Repair"
              />
            </Field>
            <Field label="Logo Link">
              <LinkField value={data.logo_link} onChange={(val) => set("logo_link", val)} />
            </Field>
          </Space>
        </WrapRow>
      </PanelCard>

      {/* SEARCH */}
      <PanelCard icon="search-line" color="#13c2c2" title="Search Bar">
        <ToggleRow
          label="Show Product Search"
          hint="Search input in the top bar (desktop) and search icon (mobile)."
          checked={search.enabled}
          onChange={(val) => setIn("search", "enabled", val)}
        />
        {search.enabled !== false && (
          <Space direction="vertical" size={16} style={{ width: "100%", marginTop: 16 }}>
            <Field label="Search Placeholders">
              <PlaceholderList
                items={search.placeholders || []}
                onChange={(val) => setIn("search", "placeholders", val)}
              />
            </Field>
            <FieldGrid min={240}>
              <Field label="No Result Prompt">
                <Input
                  value={search.no_result_text || ""}
                  onChange={(e) => setIn("search", "no_result_text", e.target.value)}
                  placeholder="e.g. Can't find product?"
                />
              </Field>
              <Field label="Request Link Text">
                <Input
                  value={search.request_link_text || ""}
                  onChange={(e) => setIn("search", "request_link_text", e.target.value)}
                  placeholder="e.g. Request Product"
                />
              </Field>
            </FieldGrid>
            <Field label="Request Link">
              <LinkField
                value={search.request_link}
                onChange={(val) => setIn("search", "request_link", val)}
              />
            </Field>
          </Space>
        )}
      </PanelCard>

      {/* HEADER ACTIONS (Wishlist, Cart, Login, etc.) */}
      <PanelCard
        icon="user-settings-line"
        color="#722ed1"
        title={`Header Actions & Buttons (${actions.length})`}
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {actions.map((item, index) => (
            <ItemBox key={item.id || index}>
              <WrapRow>
                <Field label="Action Type" flex="1 1 200px">
                  <Select
                    style={{ width: "100%" }}
                    value={item.action_type || "link"}
                    options={ACTION_TYPES}
                    onChange={(val) => acts.update(index, { action_type: val })}
                  />
                </Field>
                <Field label="Icon" flex="none">
                  <IconPicker
                    value={item.icon || undefined}
                    onChange={(val) => acts.update(index, { icon: val })}
                  />
                </Field>
                <Field label="Label (Optional)" flex="1 1 160px">
                  <Input
                    value={item.label || ""}
                    onChange={(e) => acts.update(index, { label: e.target.value })}
                    placeholder="e.g. Sign In"
                  />
                </Field>
                {(item.action_type || "link") === "link" || item.action_type === "signup" ? (
                  <Field label="Link / Page" flex="2 1 300px">
                    <LinkField
                      value={item.link}
                      onChange={(link) => acts.update(index, { link })}
                    />
                  </Field>
                ) : null}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    flex: "none",
                    height: 32,
                  }}
                >
                  <Field label="Button Style" flex="none">
                    <Select
                      style={{ width: 110 }}
                      value={item.buttonStyle || (item.isPrimary ? "primary" : "none")}
                      onChange={(val) => acts.update(index, { buttonStyle: val })}
                      options={[
                        { label: "None", value: "none" },
                        { label: "Primary", value: "primary" },
                        { label: "Secondary", value: "secondary" },
                      ]}
                    />
                  </Field>
                  <RowActions
                    index={index}
                    total={actions.length}
                    onMove={acts.move}
                    onRemove={acts.remove}
                    confirmText="Remove this action?"
                  />
                </div>
              </WrapRow>
            </ItemBox>
          ))}
          <AddItemButton
            label="Add Action (Cart, Login, etc.)"
            onClick={() =>
              acts.add({
                id: newId("act"),
                action_type: "link",
                label: "",
                icon: "",
                link: { ...EMPTY_LINK },
                isPrimary: false,
              })
            }
          />
        </Space>
      </PanelCard>

      {/* ACCOUNT MENU (signed-in dropdown) */}
      <PanelCard
        icon="account-circle-line"
        color="#b12525"
        title={`Account Menu (${accountMenu.length})`}
      >
        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
          Dropdown under the customer's name when signed in. "Log Out" is always
          added at the end. While this list is empty the site shows the default
          items.
        </Text>
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {accountMenu.map((item, index) => (
            <ItemBox key={item.id || index}>
              <WrapRow>
                <Field label="Icon" flex="none">
                  <IconPicker
                    value={item.icon || undefined}
                    onChange={(val) => account.update(index, { icon: val })}
                  />
                </Field>
                <Field label="Label" flex="1 1 180px">
                  <Input
                    value={item.label || ""}
                    onChange={(e) => account.update(index, { label: e.target.value })}
                    placeholder="e.g. My Credit Points"
                  />
                </Field>
                <Field label="Link / Page" flex="2 1 300px">
                  <LinkField
                    value={item.link}
                    onChange={(link) => account.update(index, { link })}
                  />
                </Field>
                <RowActions
                  index={index}
                  total={accountMenu.length}
                  onMove={account.move}
                  onRemove={account.remove}
                  confirmText="Remove this menu item?"
                />
              </WrapRow>
            </ItemBox>
          ))}
          {accountMenu.length === 0 && (
            <AddItemButton label="Load Default Items" onClick={loadDefaultAccountMenu} />
          )}
          <AddItemButton
            label="Add Account Menu Item"
            onClick={() =>
              account.add({
                id: newId("acc"),
                label: "",
                icon: "",
                link: { ...EMPTY_LINK },
              })
            }
          />
        </Space>
      </PanelCard>

      {/* NAVIGATION */}
      <PanelCard icon="menu-line" color="#fa8c16" title={`Navigation Menu (${navigation.length})`}>
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {navigation.map((item, index) => {
            const isDropdown = item.item_type === "dropdown";
            return (
              <ItemBox key={item.id || index}>
                <WrapRow>
                  <Field label="Menu Label" flex="1 1 170px">
                    <Input
                      value={item.label || ""}
                      onChange={(e) => nav.update(index, { label: e.target.value })}
                      placeholder="e.g. Services"
                    />
                  </Field>
                  <Field label="Type" flex="0 1 160px">
                    <Select
                      style={{ width: "100%" }}
                      value={isDropdown ? "dropdown" : "link"}
                      onChange={(val) => nav.update(index, { item_type: val })}
                      options={[
                        { label: "Direct Link", value: "link" },
                        { label: "Dropdown Menu", value: "dropdown" },
                      ]}
                    />
                  </Field>
                  {!isDropdown && (
                    <Field label="Link Destination" flex="2 1 300px">
                      <LinkField
                        value={item.link}
                        onChange={(link) => nav.update(index, { link })}
                      />
                    </Field>
                  )}
                  <div style={{ marginLeft: "auto" }}>
                    <RowActions
                      index={index}
                      total={navigation.length}
                      onMove={nav.move}
                      onRemove={nav.remove}
                      confirmText="Remove this menu item?"
                    />
                  </div>
                </WrapRow>
                {isDropdown && (
                  <div style={{ marginTop: 12, paddingLeft: 12, borderLeft: "2px solid #fca5a5" }}>
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, display: "block", marginBottom: 8 }}
                    >
                      Dropdown items
                    </Text>
                    <LinkListEditor
                      links={item.children || []}
                      onChange={(children) => nav.update(index, { children })}
                      addLabel="Add Dropdown Item"
                    />
                  </div>
                )}
              </ItemBox>
            );
          })}
          <AddItemButton
            label="Add Menu Item"
            onClick={() =>
              nav.add({
                id: newId("nav"),
                label: "",
                item_type: "link",
                link: { ...EMPTY_LINK },
                children: [],
              })
            }
          />
        </Space>
      </PanelCard>

      {/* CUSTOMER CARE */}
      <PanelCard
        icon="customer-service-2-line"
        color="#eb2f96"
        title="Customer Care (Menu Bar Right)"
      >
        <ToggleRow
          label="Show Customer Care Phone"
          checked={customerCare.enabled}
          onChange={(val) => setIn("customer_care", "enabled", val)}
        />
        {customerCare.enabled !== false && (
          <WrapRow style={{ marginTop: 16 }}>
            <Field label="Icon" flex="none">
              <IconPicker
                value={customerCare.icon || undefined}
                onChange={(val) => setIn("customer_care", "icon", val)}
              />
            </Field>
            <Field label="Label" flex="1 1 200px">
              <Input
                value={customerCare.label || ""}
                onChange={(e) => setIn("customer_care", "label", e.target.value)}
                placeholder="Customer Care"
              />
            </Field>
            <Field label="Phone Number" flex="1 1 200px">
              <Input
                value={customerCare.phone || ""}
                onChange={(e) => setIn("customer_care", "phone", e.target.value)}
                placeholder="855-636-6387"
              />
            </Field>
          </WrapRow>
        )}
      </PanelCard>
    </div>
  );
};

export default Header;
